import { authApi, mainApi } from '../index';
import { configSettings } from '@/lib/config';
import {
    AffiliateCompleteRegistrationPayload,
    AffiliateCompleteRegistrationResponse,
    AffiliateUser,
    AuthTokenUpgradeResponse,
    CheckUsernameResponse,
    LoginResponse,
    MFARecoveryPayload,
    MFARecoveryResponse,
    MFAValidateOTPPayload,
    MFAValidateOTPResponse,
    RegisterPayload,
    RegisterResponse,
    TransferAffiliateAccountPayload,
    UpdatePasswordResponse,
} from './types';
import { isAxiosError } from 'axios';

export async function loginWithPassword(email_or_username: string, password: string): Promise<LoginResponse> {
    try {
        const payload = {
            email_or_username: email_or_username.trim(),
            password,
            version: 2,
        };

        const { data } = await authApi.post('v1/login', payload);
        return data;
    } catch (err) {
        let message = 'Login failed';

        if (isAxiosError(err)) {
            if (err.code === 'ERR_NETWORK') {
                message = `Network error: Could not connect to auth server at ${configSettings.authApiUrl}. Please check your API configuration.`;
            } else {
                message = err.response?.data?.message || err.response?.data?.error || `API error: ${err.message}`;
            }
        } else if (err instanceof Error) {
            message = err.message;
        }

        console.error('Login error:', {
            error: err,
            apiUrl: configSettings.authApiUrl,
        });

        throw new Error(message);
    }
}

export async function checkAuthTokenUpgrade(authToken: string): Promise<AuthTokenUpgradeResponse> {
    try {
        const { data } = await authApi.get('v1/authentication/upgrade-auth-token', { params: { authToken } });
        return data;
    } catch (err) {
        let message = 'Auth token check upgrade failed';

        if (isAxiosError(err)) {
            if (err.code === 'ERR_NETWORK') {
                message = `Network error: Could not connect to auth server at ${configSettings.authApiUrl}. Please check your API configuration.`;
            } else {
                message = err.response?.data?.message || err.response?.data?.error || `API error: ${err.message}`;
            }
        } else if (err instanceof Error) {
            message = err.message;
        }

        console.error('Login error:', {
            error: err,
            apiUrl: configSettings.authApiUrl,
        });

        throw new Error(message);
    }
}

export async function sendResetPasswordEmail(email: string): Promise<void> {
    try {
        await mainApi.post('v1/authentication/generate-password-reset-token', { email });
    } catch (error) {
        console.error('Error reseting password:', error);
        throw error;
    }
}

export async function tokenUpdatePassword(
    passwordResetToken: string,
    password: string,
    confirmPassword: string
): Promise<UpdatePasswordResponse> {
    try {
        // Overrides Axios token for specalized one
        const headers = {
            Authorization: `Bearer ${passwordResetToken}`,
        };
        const payload = {
            password: password,
            confirm_password: confirmPassword,
        };

        const response = await mainApi.post('v1/authentication/update-password', payload, { headers });
        return response.data;
    } catch (error) {
        console.error('Error updating password:', error);
        throw error;
    }
}

export async function checkUsernameAvailability(username: string, email?: string): Promise<CheckUsernameResponse> {
    try {
        const payload: { username?: string; email?: string } = {};
        if (username) payload.username = username.trim();
        if (email) payload.email = email.trim();

        const { data } = await mainApi.post('v1/authentication/check-unique-taken', payload);
        return data;
    } catch (error) {
        console.error('Error checking username availability:', error);
        throw error;
    }
}

export async function register(payload: RegisterPayload): Promise<RegisterResponse> {
    try {
        const { data } = await mainApi.post('v1/authentication/register', payload);
        return data;
    } catch (error) {
        console.error('Error registering user:', error);
        throw error;
    }
}

// Affiliate Registration Functions

/**
 * Get affiliate user details using the registration token
 *
 * @param token - JWT token from the affiliate registration link
 * @returns Promise resolving to affiliate user data
 * @throws Error if the API request fails
 */
export async function getAffiliateUser(token: string): Promise<AffiliateUser> {
    const { data } = await mainApi.get('v1/users/get_user', {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data as AffiliateUser;
}

/**
 * Complete affiliate registration (create new account)
 *
 * @param payload - Registration data including name, email, password, phone
 * @param token - JWT token from the affiliate registration link
 * @returns Promise resolving to registration response with auth token
 * @throws Error if the API request fails
 */
export async function completeAffiliateRegistration(
    payload: AffiliateCompleteRegistrationPayload,
    token: string
): Promise<AffiliateCompleteRegistrationResponse> {
    const { data } = await mainApi.post('v1/authentication/affiliate/complete-registration', payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data;
}

/**
 * Transfer affiliate account to existing Stan user
 *
 * @param payload - Email and username of the existing Stan account
 * @param token - JWT token from the affiliate registration link
 * @returns Promise that resolves when transfer is complete
 * @throws Error if the API request fails
 */
export async function transferAffiliateAccount(payload: TransferAffiliateAccountPayload, token: string): Promise<void> {
    await mainApi.post('/v1/resell-rights/transfer-affiliate-account', payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

// MFA Functions

/**
 * Submit MFA recovery code to authenticate
 *
 * @param payload - Recovery text payload
 * @param token - MFA token from login response
 * @returns Promise resolving to new auth token
 * @throws Error if the API request fails
 */
export async function submitMFARecovery(payload: MFARecoveryPayload, token: string): Promise<MFARecoveryResponse> {
    const { data } = await authApi.post('v1/mfa/recovery', payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data;
}

/**
 * Validate MFA OTP code
 *
 * @param payload - OTP payload with code and TTL settings
 * @param endpoint - The login endpoint to validate against
 * @param token - MFA token from login response
 * @returns Promise resolving to new auth token
 * @throws Error if the API request fails
 */
export async function validateMFAOTP(
    payload: MFAValidateOTPPayload,
    endpoint: string,
    token: string
): Promise<MFAValidateOTPResponse> {
    const { data } = await authApi.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data;
}
